import { useState, useCallback, useMemo } from 'react';
import { CATION_DATA, PRECIPITATE_COLORS } from '@/config/experiments/chem1131/experiment7.config';

// Define the solubility matrix data
const SOLUBILITY_DATA: Record<string, Record<string, { soluble: boolean; precipitate?: string; color?: string; description?: string }>> = {
  'Al3+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'Al2S3', color: '#FFFFFF', description: 'Hydrolyzes to Al(OH)3' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'Al2(CO3)3', color: '#FFFFFF', description: 'Hydrolyzes' },
    'PO43-': { soluble: false, precipitate: 'AlPO4', color: '#FFFFFF', description: 'White precipitate' },
    'OH-': { soluble: false, precipitate: 'Al(OH)3', color: '#F8F8FF', description: 'White gelatinous' },
  },
  'Ba2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'BaS', color: '#FAFAFA', description: 'Slightly soluble' },
    'SO42-': { soluble: false, precipitate: 'BaSO₄', color: '#FEFEFE', description: 'Fine white precipitate' },
    'CO32-': { soluble: false, precipitate: 'BaCO₃', color: '#FEFEFE', description: 'White precipitate' },
    'PO43-': { soluble: false, precipitate: 'Ba3(PO4)2', color: '#FFFFFF', description: 'White precipitate' },
    'OH-': { soluble: true },
  },
  'Ca2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'CaS', color: '#FAFAFA', description: 'Slightly soluble' },
    'SO42-': { soluble: false, precipitate: 'CaSO₄', color: '#F5F5F5', description: 'Slightly soluble' },
    'CO32-': { soluble: false, precipitate: 'CaCO₃', color: '#FFFFFF', description: 'White precipitate' },
    'PO43-': { soluble: false, precipitate: 'Ca3(PO4)2', color: '#FFFFFF', description: 'White precipitate' },
    'OH-': { soluble: false, precipitate: 'Ca(OH)2', color: '#FFFAFA', description: 'Slightly soluble' },
  },
  'Co2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'CoS', color: '#1F1F1F', description: 'Black precipitate' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'CoCO3', color: '#FFB6C1', description: 'Pink precipitate' },
    'PO43-': { soluble: false, precipitate: 'Co3(PO4)2', color: '#DDA0DD', description: 'Pink-violet' },
    'OH-': { soluble: false, precipitate: 'Co(OH)2', color: '#DDA0DD', description: 'Rose to blue-green' },
  },
  'Cu2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: false, precipitate: 'CuI', color: '#FFFAF0', description: 'White, reduces to CuI' },
    'S2-': { soluble: false, precipitate: 'CuS', color: '#1A1A1A', description: 'Black precipitate' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'CuCO3', color: '#7FFFD4', description: 'Blue-green' },
    'PO43-': { soluble: false, precipitate: 'Cu3(PO4)2', color: '#87CEEB', description: 'Blue precipitate' },
    'OH-': { soluble: false, precipitate: 'Cu(OH)2', color: '#6495ED', description: 'Light blue gelatinous' },
  },
  'Fe3+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true, description: 'Redox reaction occurs' },
    'S2-': { soluble: false, precipitate: 'Fe2S3', color: '#0D0D0D', description: 'Black, unstable' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'Fe2(CO3)3', color: '#B7410E', description: 'Rust brown' },
    'PO43-': { soluble: false, precipitate: 'FePO4', color: '#F5F5DC', description: 'Yellowish white' },
    'OH-': { soluble: false, precipitate: 'Fe(OH)3', color: '#B7410E', description: 'Rust-colored precipitate' },
  },
  'Mg2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'MgS', color: '#FAFAFA', description: 'Hydrolyzes' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'MgCO3', color: '#FFFFFF', description: 'White precipitate' },
    'PO43-': { soluble: false, precipitate: 'Mg3(PO4)2', color: '#FFFFFF', description: 'White precipitate' },
    'OH-': { soluble: false, precipitate: 'Mg(OH)2', color: '#FFFAF0', description: 'White precipitate' },
  },
  'Mn2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'MnS', color: '#FFDAB9', description: 'Flesh-colored' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'MnCO3', color: '#FFEFD5', description: 'Pale pink' },
    'PO43-': { soluble: false, precipitate: 'Mn3(PO4)2', color: '#FFF0F5', description: 'Pink-white' },
    'OH-': { soluble: false, precipitate: 'Mn(OH)2', color: '#FFFAF0', description: 'White, oxidizes to brown' },
  },
  'Ni2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'NiS', color: '#2D2D2D', description: 'Black precipitate' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'NiCO3', color: '#98FB98', description: 'Light green' },
    'PO43-': { soluble: false, precipitate: 'Ni3(PO4)2', color: '#90EE90', description: 'Green precipitate' },
    'OH-': { soluble: false, precipitate: 'Ni(OH)2', color: '#98FB98', description: 'Pale green precipitate' },
  },
  'Ag+': {
    'Cl-': { soluble: false, precipitate: 'AgCl', color: '#FFFFFF', description: 'Curdy white precipitate' },
    'Br-': { soluble: false, precipitate: 'AgBr', color: '#FFFDD0', description: 'Cream/pale yellow' },
    'I-': { soluble: false, precipitate: 'AgI', color: '#FFFF00', description: 'Bright yellow' },
    'S2-': { soluble: false, precipitate: 'Ag2S', color: '#0D0D0D', description: 'Black precipitate' },
    'SO42-': { soluble: false, precipitate: 'Ag2SO4', color: '#F5F5F5', description: 'Slightly soluble' },
    'CO32-': { soluble: false, precipitate: 'Ag2CO3', color: '#FFFACD', description: 'White-yellow' },
    'PO43-': { soluble: false, precipitate: 'Ag3PO4', color: '#FFFF00', description: 'Yellow precipitate' },
    'OH-': { soluble: false, precipitate: 'AgOH', color: '#3D3D3D', description: 'Decomposes to Ag2O (brown)' },
  },
  'Zn2+': {
    'Cl-': { soluble: true },
    'Br-': { soluble: true },
    'I-': { soluble: true },
    'S2-': { soluble: false, precipitate: 'ZnS', color: '#FFFAFA', description: 'White precipitate' },
    'SO42-': { soluble: true },
    'CO32-': { soluble: false, precipitate: 'ZnCO3', color: '#FFFFFF', description: 'White precipitate' },
    'PO43-': { soluble: false, precipitate: 'Zn3(PO4)2', color: '#FFFFFF', description: 'White precipitate' },
    'OH-': { soluble: false, precipitate: 'Zn(OH)2', color: '#F5F5F5', description: 'White gelatinous' },
  },
};

const CATIONS = ['Al3+', 'Ba2+', 'Ca2+', 'Co2+', 'Cu2+', 'Fe3+', 'Mg2+', 'Mn2+', 'Ni2+', 'Ag+', 'Zn2+'];
const ANIONS = ['Cl-', 'Br-', 'I-', 'S2-', 'SO42-', 'CO32-', 'PO43-', 'OH-'];

interface SolubilityGridProps {
  onCellClick?: (cation: string, anion: string, data: typeof SOLUBILITY_DATA[string][string]) => void;
  testedCells?: Set<string>;
  showAll?: boolean;
}

export default function SolubilityGrid({ onCellClick, testedCells = new Set(), showAll = false }: SolubilityGridProps) {
  const [hoveredCell, setHoveredCell] = useState<{ cation: string; anion: string } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ cation: string; anion: string } | null>(null);

  const handleCellClick = useCallback((cation: string, anion: string) => {
    const data = SOLUBILITY_DATA[cation]?.[anion];
    setSelectedCell({ cation, anion });
    if (onCellClick && data) {
      onCellClick(cation, anion, data);
    }
  }, [onCellClick]);

  const getCellKey = (cation: string, anion: string) => `${cation}-${anion}`;

  const renderCell = (cation: string, anion: string) => {
    const data = SOLUBILITY_DATA[cation]?.[anion];
    const key = getCellKey(cation, anion);
    const isTested = testedCells.has(key) || showAll;
    const isHovered = hoveredCell?.cation === cation && hoveredCell?.anion === anion;
    const isSelected = selectedCell?.cation === cation && selectedCell?.anion === anion;

    if (!data) return <td key={key} className="cell empty">-</td>;

    return (
      <td
        key={key}
        className={`cell ${isTested ? 'tested' : 'untested'} ${data.soluble ? 'soluble' : 'insoluble'} ${isHovered ? 'hovered' : ''} ${isSelected ? 'selected' : ''}`}
        onClick={() => handleCellClick(cation, anion)}
        onMouseEnter={() => setHoveredCell({ cation, anion })}
        onMouseLeave={() => setHoveredCell(null)}
        style={{
          backgroundColor: isTested && !data.soluble && data.color ? data.color : undefined,
        }}
      >
        {isTested ? (
          data.soluble ? (
            <span className="soluble-marker">S</span>
          ) : (
            <span className="precipitate-marker" title={data.description}>
              {data.precipitate?.substring(0, 4) || 'ppt'}
            </span>
          )
        ) : (
          <span className="unknown-marker">?</span>
        )}
      </td>
    );
  };

  // Get selected cell data for display
  const selectedData = selectedCell
    ? SOLUBILITY_DATA[selectedCell.cation]?.[selectedCell.anion]
    : null;

  return (
    <div className="solubility-grid-container">
      <div className="grid-wrapper">
        <table className="solubility-table">
          <thead>
            <tr>
              <th className="corner-cell">
                <span className="diagonal-text">
                  <span className="anion-label">Anions</span>
                  <span className="cation-label">Cations</span>
                </span>
              </th>
              {CATIONS.map(cation => (
                <th key={cation} className="cation-header">
                  <span className="ion-symbol">{cation.replace(/\d+[+-]/, '')}</span>
                  <sup className="ion-charge">{cation.match(/\d+[+-]/)?.[0]}</sup>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ANIONS.map(anion => (
              <tr key={anion}>
                <th className="anion-header">
                  <span className="ion-symbol">{anion.replace(/\d*[+-]/, '')}</span>
                  <sup className="ion-charge">{anion.match(/\d*[+-]/)?.[0]}</sup>
                </th>
                {CATIONS.map(cation => renderCell(cation, anion))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="grid-legend">
        <div className="legend-item">
          <span className="legend-color soluble-bg">S</span>
          <span>Soluble (no precipitate)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color insoluble-bg">ppt</span>
          <span>Insoluble (precipitate forms)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color unknown-bg">?</span>
          <span>Not yet tested</span>
        </div>
      </div>

      {/* Selected Cell Info */}
      {selectedCell && selectedData && (
        <div className="cell-info">
          <h4>
            {selectedCell.cation} + {selectedCell.anion}
          </h4>
          {selectedData.soluble ? (
            <p className="result soluble">Soluble - no precipitate forms</p>
          ) : (
            <>
              <p className="result insoluble">
                Precipitate: <strong>{selectedData.precipitate}</strong>
              </p>
              {selectedData.description && (
                <p className="description">{selectedData.description}</p>
              )}
              {selectedData.color && (
                <div className="color-swatch" style={{ backgroundColor: selectedData.color }}>
                  Precipitate color
                </div>
              )}
            </>
          )}
        </div>
      )}

      <style>{`
        .solubility-grid-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          width: 100%;
        }

        .grid-wrapper {
          overflow-x: auto;
          padding: 10px;
        }

        .solubility-table {
          border-collapse: collapse;
          font-size: 11px;
          background: var(--bg-elevated);
          border-radius: 8px;
          overflow: hidden;
        }

        .solubility-table th,
        .solubility-table td {
          border: 1px solid var(--border);
          padding: 6px 8px;
          text-align: center;
          min-width: 45px;
        }

        .corner-cell {
          background: var(--bg-surface);
          position: relative;
          width: 60px;
          height: 50px;
        }

        .diagonal-text {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .diagonal-text::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom right, transparent 49%, var(--border) 49%, var(--border) 51%, transparent 51%);
        }

        .anion-label {
          position: absolute;
          top: 4px;
          right: 4px;
          font-size: 8px;
          color: var(--text-secondary);
        }

        .cation-label {
          position: absolute;
          bottom: 4px;
          left: 4px;
          font-size: 8px;
          color: var(--text-secondary);
        }

        .cation-header,
        .anion-header {
          background: var(--bg-surface);
          font-weight: 600;
          color: var(--text-primary);
        }

        .ion-symbol {
          font-size: 12px;
        }

        .ion-charge {
          font-size: 9px;
          color: var(--text-secondary);
        }

        .cell {
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .cell.untested {
          background: var(--bg-surface);
        }

        .cell.tested.soluble {
          background: rgba(0, 168, 107, 0.15);
        }

        .cell.tested.insoluble {
          border: 2px solid rgba(0,0,0,0.3);
        }

        .cell.hovered {
          transform: scale(1.1);
          z-index: 10;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        .cell.selected {
          outline: 2px solid var(--secondary);
          outline-offset: -2px;
        }

        .soluble-marker {
          color: var(--secondary);
          font-weight: 600;
        }

        .precipitate-marker {
          color: #333;
          font-weight: 600;
          font-size: 9px;
        }

        .unknown-marker {
          color: var(--text-secondary);
          opacity: 0.5;
        }

        .grid-legend {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .legend-color {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
        }

        .legend-color.soluble-bg {
          background: rgba(0, 168, 107, 0.15);
          color: var(--secondary);
        }

        .legend-color.insoluble-bg {
          background: #DDD;
          color: #333;
          border: 2px solid rgba(0,0,0,0.3);
        }

        .legend-color.unknown-bg {
          background: var(--bg-surface);
          color: #999;
          border: 1px solid var(--border);
        }

        .cell-info {
          background: var(--bg-elevated);
          border-radius: 8px;
          padding: 16px;
          border-left: 3px solid var(--secondary);
          max-width: 350px;
          margin: 0 auto;
        }

        .cell-info h4 {
          margin: 0 0 8px 0;
          color: var(--text-primary);
          font-size: 14px;
        }

        .cell-info .result {
          margin: 0 0 8px 0;
          font-size: 13px;
        }

        .cell-info .result.soluble {
          color: var(--secondary);
        }

        .cell-info .result.insoluble {
          color: var(--text-primary);
        }

        .cell-info .description {
          margin: 0;
          font-size: 12px;
          color: var(--text-secondary);
          font-style: italic;
        }

        .color-swatch {
          margin-top: 8px;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 11px;
          text-align: center;
          border: 1px solid var(--border);
        }
      `}</style>
    </div>
  );
}

export { SOLUBILITY_DATA, CATIONS, ANIONS };
